import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  UserPlus,
  Search,
  Filter,
  MoreHorizontal,
  Phone,
  MapPin,
  ChevronRight,
  FileText,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusOptions = [
  { value: "all", label: "All Statuses" },
  { value: "potential", label: "Potential" },
  { value: "active", label: "Active" },
  { value: "discharged", label: "Discharged" },
  { value: "on_hold", label: "On Hold" },
  { value: "inactive", label: "Inactive" },
  { value: "non_admit", label: "Non-Admit" },
];

export default function ClientsPage() {
  const { canCreate, hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "active");

  useEffect(() => {
    fetchClients();
  }, [statusFilter]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      if (searchQuery) {
        params.append("search", searchQuery);
      }
      
      const response = await axios.get(`${API}/clients?${params.toString()}`);
      const sorted = [...response.data].sort((a, b) => {
        const aLast = (a.last_name || "").toLowerCase();
        const bLast = (b.last_name || "").toLowerCase();
        if (aLast !== bLast) return aLast.localeCompare(bLast);
        return (a.first_name || "").toLowerCase().localeCompare((b.first_name || "").toLowerCase());
      });
      setClients(sorted);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchClients();
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    if (value === "all") {
      searchParams.delete("status");
    } else {
      searchParams.set("status", value);
    }
    setSearchParams(searchParams);
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      potential: "badge-potential",
      active: "badge-active",
      discharged: "badge-discharged",
      on_hold: "badge-on_hold",
      inactive: "badge-inactive",
      non_admit: "badge-non_admit",
    };
    return classes[status] || "badge-potential";
  };

  const formatStatus = (status) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Layout>
      {/* Page Header */}
      <div className="page-header">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="page-title">Clients</h1>
            <p className="page-subtitle">
              Manage your client database and track their care journey.
            </p>
          </div>
          <div className="flex gap-3">
            {hasPermission("referrals", "create") && (
              <Link to="/referrals">
                <Button className="gap-2" data-testid="new-referral-btn">
                  <FileText className="w-4 h-4" />
                  New Referral
                </Button>
              </Link>
            )}
            {canCreate("clients") && (
              <Link to="/clients/new">
                <Button variant="outline" className="gap-2" data-testid="add-client-btn">
                  <UserPlus className="w-4 h-4" />
                  Add New Potential
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search by name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="client-search-input"
              />
            </div>
          </form>

          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Client List */}
      <div className="bg-white rounded-xl border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : clients.length === 0 ? (
          <div className="empty-state py-16">
            <div className="empty-state-icon">
              <Users className="w-8 h-8 text-slate-400" />
            </div>
            <p className="empty-state-title">No clients found</p>
            <p className="empty-state-description">
              {statusFilter === "active"
                ? "No active clients yet. Try switching the filter to Potential or All Statuses."
                : statusFilter !== "all"
                ? `No clients with "${formatStatus(statusFilter)}" status.`
                : "Start by adding your first client to the system."}
            </p>
            {canCreate("clients") && (
              <Link to="/clients/new" className="mt-4">
                <Button size="sm" data-testid="empty-add-client-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Client
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {/* Table Header - Desktop */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-400">
              <div className="col-span-4">Client</div>
              <div className="col-span-2">Phone</div>
              <div className="col-span-2">Location</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Actions</div>
            </div>

            {/* Client Rows */}
            {clients.map((client) => (
              <Link
                key={client.id}
                to={`/clients/${client.id}`}
                className="block hover:bg-slate-50 transition-colors"
                data-testid={`client-row-${client.id}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 items-center">
                  {/* Client Info */}
                  <div className="md:col-span-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
                      {client.first_name[0]}
                      {client.last_name[0]}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">
                        {client.first_name} {client.last_name}
                      </p>
                      <p className="text-sm text-slate-500">
                        DOB: {client.dob || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="md:col-span-2 flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400 md:hidden" />
                    <span>{client.phone || "No phone"}</span>
                  </div>

                  {/* Location */}
                  <div className="md:col-span-2 flex items-center gap-2 text-sm text-slate-600">
                    <MapPin className="w-4 h-4 text-slate-400 md:hidden" />
                    <span>{client.city || client.state || "No location"}</span>
                  </div>

                  {/* Status */}
                  <div className="md:col-span-2">
                    <span className={`badge ${getStatusBadgeClass(client.status)}`}>
                      {formatStatus(client.status)}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="md:col-span-2 flex items-center justify-end gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={(e) => e.preventDefault()}
                          data-testid={`client-menu-${client.id}`}
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>View Profile</DropdownMenuItem>
                        <DropdownMenuItem>Edit Client</DropdownMenuItem>
                        <DropdownMenuItem>Create Referral</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Results Count */}
      {!loading && clients.length > 0 && (
        <p className="text-sm text-slate-500 mt-4">
          Showing {clients.length} client{clients.length !== 1 ? "s" : ""}
        </p>
      )}
    </Layout>
  );
}
